import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Search,
  Filter,
  Calendar,
  Check,
  Play,
  Trash,
  Edit,
  Trash2,
  ChevronDown,
  Clock,
  AlertCircle,
  CheckCircle,
  PlayCircle,
  EyeOff,
  MoreVertical,
  User,
  GraduationCap,
  FileText,
  Target,
  Zap,
  BookOpen
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { toast } from 'react-hot-toast';
import TaskSynchronizationService from '../../services/TaskSynchronizationService';

const TaskManagerPanel = ({
  isOpen,
  onClose,
  userId,
  timeline = [],
  onTaskUpdate,
  initialTab = 'all'
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [sortBy, setSortBy] = useState('due_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [epicFilter, setEpicFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  // DEBUG: Refs for tracking
  const renderCountRef = useRef(0);
  const searchInputRef = useRef(null);
  const componentMountTimeRef = useRef(Date.now());

  // DEBUG: Track component mount/unmount
  useEffect(() => {
    console.log('🔧 [DEBUG] TaskManagerPanel MOUNTED at', new Date().toISOString());
    return () => {
      console.log('🔧 [DEBUG] TaskManagerPanel UNMOUNTED at', new Date().toISOString());
    };
  }, []);

  // DEBUG: Track every render
  renderCountRef.current += 1;
  console.log('🔧 [DEBUG] TaskManagerPanel RENDER #', renderCountRef.current, {
    isOpen,
    searchTerm,
    activeTab,
    timelineLength: timeline?.length,
    userId,
  });

  // DEBUG: Track searchTerm changes
  useEffect(() => {
    console.log('🔧 [DEBUG] searchTerm CHANGED to:', searchTerm);
  }, [searchTerm]);

  // DEBUG: Track focus/blur on search input
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return;

    const handleFocus = (e) => {
      console.log('🔧 [DEBUG] Search input FOCUSED', {
        activeElement: document.activeElement,
        inputRef: searchInputRef.current,
        timestamp: Date.now()
      });
    };

    const handleBlur = (e) => {
      console.log('🔧 [DEBUG] Search input BLURRED', {
        relatedTarget: e.relatedTarget,
        timestamp: Date.now()
      });
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, []);

  // DEBUG: Track timeline prop changes
  const timelineRef = useRef(timeline);
  useEffect(() => {
    if (timeline !== timelineRef.current) {
      console.log('🔧 [DEBUG] timeline prop CHANGED', {
        oldLength: timelineRef.current?.length,
        newLength: timeline?.length,
        isNewReference: timeline !== timelineRef.current
      });
      timelineRef.current = timeline;
    }
  }, [timeline]);

  // Update activeTab when initialTab changes (for external triggers)
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      console.log('🔧 [DEBUG] activeTab changing from', activeTab, 'to', initialTab);
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Handle Escape key to close panel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Get category info with distinctive icons and coherent colors
  const getCategoryInfo = (category) => {
    const categoryMap = {
      'application': { icon: FileText, color: 'bg-azure-100 text-azure-700', label: 'Application' },
      'essay': { icon: Edit, color: 'bg-violet-100 text-violet-700', label: 'Essay' },
      'testing': { icon: Target, color: 'bg-orange-100 text-orange-700', label: 'Testing' },
      'financial_aid': { icon: Zap, color: 'bg-emerald-100 text-emerald-700', label: 'Financial Aid' },
      'profile': { icon: User, color: 'bg-indigo-100 text-indigo-700', label: 'Profile' },
      'research': { icon: BookOpen, color: 'bg-teal-100 text-teal-700', label: 'Research' },
      'general': { icon: Clock, color: 'bg-slate-100 text-slate-700', label: 'General' }
    };
    return categoryMap[category] || categoryMap['general'];
  };

  // Check if task is completed
  const isTaskCompleted = (task) => {
    return task.completed || task.completion_status === 'completed' || task.status === 'completed' || task.workflow_phase === 'complete';
  };

  // Check if task is overdue
  const isTaskOverdue = (task) => {
    return task.scheduled_date && 
           parseISO(task.scheduled_date) < new Date() && 
           !isTaskCompleted(task) &&
           task.status !== 'dismissed';
  };

  // Get epic info with distinctive icons and systematic colors
  const getEpicInfo = (epicId) => {
    const epicMap = {
      'foundation': { label: 'Profile & Foundation', color: 'bg-indigo-100 text-indigo-700 border border-indigo-200', icon: User },
      'college_research': { label: 'College Research', color: 'bg-teal-100 text-teal-700 border border-teal-200', icon: BookOpen },
      'essays': { label: 'Essays', color: 'bg-violet-100 text-violet-700 border border-violet-200', icon: Edit },
      'applications': { label: 'Applications', color: 'bg-azure-100 text-azure-700 border border-azure-200', icon: FileText },
      'testing': { label: 'Testing', color: 'bg-orange-100 text-orange-700 border border-orange-200', icon: Target },
      'financial_aid': { label: 'Financial Aid', color: 'bg-emerald-100 text-emerald-700 border border-emerald-200', icon: Zap },
      // Legacy mapping for backwards compatibility
      'college_list': { label: 'College Research', color: 'bg-teal-100 text-teal-700 border border-teal-200', icon: BookOpen },
      'submission': { label: 'Applications', color: 'bg-azure-100 text-azure-700 border border-azure-200', icon: FileText }
    };
    return epicMap[epicId] || { label: 'General', color: 'bg-slate-100 text-slate-700 border border-slate-200', icon: Clock };
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    console.log('🔧 [DEBUG] filteredAndSortedTasks useMemo RECALCULATING', {
      timelineLength: timeline?.length,
      searchTerm,
      activeTab,
      categoryFilter,
      epicFilter,
      priorityFilter,
      sortBy,
      sortOrder
    });
    let filtered = timeline.filter(task => {
      // Tab filtering
      if (activeTab === 'ready') return task.completion_status === 'not_started' && task.status !== 'dismissed';
      if (activeTab === 'progress') return task.completion_status === 'in_progress' || task.status === 'in_progress';
      if (activeTab === 'completed') return task.completion_status === 'completed';
      if (activeTab === 'dismissed') return task.status === 'dismissed';
      if (activeTab === 'overdue') return task.scheduled_date && 
        parseISO(task.scheduled_date) < new Date() && 
        !(task.completed || task.completion_status === 'completed' || task.status === 'completed' || task.workflow_phase === 'complete') &&
        task.status !== 'dismissed';
      if (activeTab === 'all') return task.status !== 'dismissed' && !isTaskCompleted(task); // Show all except dismissed and completed

      return true;
    });

    // Search filtering
    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.college_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filtering
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(task => task.category === categoryFilter);
    }

    // Epic filtering
    if (epicFilter !== 'all') {
      filtered = filtered.filter(task => task.epic_id === epicFilter);
    }

    // Priority filtering
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'due_date':
          aVal = a.scheduled_date || a.due_date || '9999-12-31';
          bVal = b.scheduled_date || b.due_date || '9999-12-31';
          break;
        case 'title':
          aVal = a.title || '';
          bVal = b.title || '';
          break;
        case 'priority':
          const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case 'category':
          aVal = a.category || '';
          bVal = b.category || '';
          break;
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [timeline, activeTab, searchTerm, categoryFilter, epicFilter, priorityFilter, sortBy, sortOrder]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const categories = [...new Set(timeline.map(t => t.category).filter(Boolean))];
    const epics = [...new Set(timeline.map(t => t.epic_id).filter(Boolean))];
    const priorities = [...new Set(timeline.map(t => t.priority).filter(Boolean))];
    
    return { categories, epics, priorities };
  }, [timeline]);

  // Task counts for tabs
  const taskCounts = useMemo(() => {
    console.log('🔧 [DEBUG] taskCounts useMemo RECALCULATING');
    console.log('🔍 [TaskManagerPanel] Calculating task counts from timeline:', timeline?.length || 0, 'tasks');
    
    if (timeline?.length > 0) {
      console.log('🔍 [TaskManagerPanel] Sample tasks:', timeline.slice(0, 3).map(t => ({
        id: t.id,
        title: t.title,
        completed: t.completed,
        completion_status: t.completion_status,
        status: t.status
      })));
    }
    
    const counts = {
      all: timeline.filter(t => t.status !== 'dismissed' && !(t.completed || t.completion_status === 'completed' || t.status === 'completed' || t.workflow_phase === 'complete')).length,
      ready: timeline.filter(t => t.completion_status === 'not_started' && t.status !== 'dismissed').length,
      progress: timeline.filter(t => t.completion_status === 'in_progress' || t.status === 'in_progress').length,
      completed: timeline.filter(t => t.completed === true || t.completion_status === 'completed').length,
      dismissed: timeline.filter(t => t.status === 'dismissed').length,
      overdue: timeline.filter(t => 
        t.scheduled_date && 
        parseISO(t.scheduled_date) < new Date() && 
        !(t.completed || t.completion_status === 'completed' || t.status === 'completed' || t.workflow_phase === 'complete') &&
        t.status !== 'dismissed'
      ).length
    };
    
    console.log('🔍 [TaskManagerPanel] Task counts calculated:', counts);
    return counts;
  }, [timeline]);

  // Dashboard metrics for summary cards
  const dashboardMetrics = useMemo(() => {
    const today = new Date();
    const upcoming = timeline.filter(t => 
      t.scheduled_date && 
      parseISO(t.scheduled_date) >= today && 
      t.status !== 'dismissed' && 
      t.completion_status !== 'completed'
    ).length;
    
    const inProgress = taskCounts.progress;
    const needAttention = taskCounts.overdue;
    
    return {
      upcoming,
      inProgress, 
      needAttention
    };
  }, [timeline, taskCounts]);

  // Handle task actions
  const handleTaskAction = async (taskId, action, newValue = null) => {
    setLoading(true);
    try {
      let updates = {};
      
      switch (action) {
        case 'complete':
          updates = { 
            completed: true,
            completion_status: 'completed',
            workflow_phase: 'complete',
            status: 'completed',
            completed_at: new Date().toISOString(),
            completion_date: new Date().toISOString()
          };
          toast.success('Task marked as complete!');
          break;
          
        case 'start':
          updates = { 
            completion_status: 'in_progress',
            status: 'in_progress',
            started_at: new Date().toISOString()
          };
          toast.success('Task started!');
          break;
          
        case 'dismiss':
          updates = { 
            status: 'dismissed',
            dismissed_at: new Date().toISOString()
          };
          toast.success('Task dismissed');
          break;
          
        case 'reschedule':
          updates = { 
            scheduled_date: newValue
          };
          toast.success('Task rescheduled');
          break;
          
        case 'priority':
          updates = { 
            priority: newValue
          };
          toast.success('Task priority updated');
          break;
          
        default:
          return;
      }

      // Update via TaskSynchronizationService
      await TaskSynchronizationService.updateFromEpic(taskId, updates);
      
      // Notify parent of update
      if (onTaskUpdate) {
        onTaskUpdate();
      }
      
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action, newValue = null) => {
    if (selectedTasks.size === 0) {
      toast.error('No tasks selected');
      return;
    }

    setLoading(true);
    try {
      const taskIds = Array.from(selectedTasks);
      
      for (const taskId of taskIds) {
        await handleTaskAction(taskId, action, newValue);
      }
      
      setSelectedTasks(new Set());
      toast.success(`${taskIds.length} tasks updated`);
      
    } catch (error) {
      console.error('Error with bulk action:', error);
      toast.error('Failed to update tasks');
    } finally {
      setLoading(false);
    }
  };

  // Toggle task selection
  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  // Select all visible tasks
  const selectAllVisible = () => {
    const visibleTaskIds = filteredAndSortedTasks.map(task => task.id);
    setSelectedTasks(new Set(visibleTaskIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedTasks(new Set());
  };

  // Get date display
  const getDateDisplay = (task) => {
    // If task is completed, show completion date
    if (isTaskCompleted(task) && task.completed_at) {
      const completedDate = parseISO(task.completed_at);
      return `Completed ${format(completedDate, 'MMM d')}`;
    }
    
    // Otherwise show scheduled or due date
    const date = task.scheduled_date || task.due_date;
    if (!date) return 'No date set';
    
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) return 'Today';
    if (isTomorrow(parsedDate)) return 'Tomorrow';
    if (isThisWeek(parsedDate)) return format(parsedDate, 'EEEE');
    return format(parsedDate, 'MMM d');
  };

  // Calculate days late for overdue tasks
  const getDaysLate = (task) => {
    const date = task.scheduled_date || task.due_date;
    if (!date) return 0;
    
    const taskDate = parseISO(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    taskDate.setHours(0, 0, 0, 0);
    
    if (taskDate >= today) return 0;
    
    const diffTime = today.getTime() - taskDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get priority color with modern styling
  const getPriorityColor = (priority) => {
    const colors = {
      'critical': 'text-red-700 bg-red-100 border border-red-200 font-semibold',
      'high': 'text-orange-700 bg-orange-100 border border-orange-200 font-medium', 
      'medium': 'text-azure-700 bg-azure-100 border border-azure-200 font-medium',
      'low': 'text-slate-700 bg-stone-100 border border-stone-200 font-normal'
    };
    return colors[priority] || colors['medium'];
  };

  // DEBUG: Track when component returns null vs renders
  if (!isOpen) {
    console.log('🔧 [DEBUG] TaskManagerPanel returning null (isOpen=false)');
    return null;
  }

  console.log('🔧 [DEBUG] TaskManagerPanel rendering JSX');

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        key="task-manager-panel"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Task Manager</h2>
            <p className="text-sm text-slate-600 mt-1">
              Manage all your timeline tasks in one place
            </p>
          </div>
          <div className="flex items-center gap-3">
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs and Controls */}
        <div className="px-6 py-4 border-b border-stone-200">
          {/* Tabs */}
          <div className="flex items-center gap-1 mb-4">
            {[
              { key: 'all', label: 'All Tasks', count: taskCounts.all },
              { key: 'overdue', label: 'Overdue', count: taskCounts.overdue },
              { key: 'ready', label: 'Ready to Start', count: taskCounts.ready },
              { key: 'progress', label: 'In Progress', count: taskCounts.progress },
              { key: 'completed', label: 'Completed', count: taskCounts.completed },
              { key: 'dismissed', label: 'Dismissed', count: taskCounts.dismissed }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                  activeTab === tab.key
                    ? tab.key === 'overdue' 
                      ? 'bg-red-100 text-red-700'
                      : 'bg-azure-100 text-azure-700'
                    : tab.key === 'overdue' && tab.count > 0
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-slate-600 hover:bg-stone-100'
                }`}
              >
                {tab.icon && <tab.icon className="w-4 h-4 mr-1" />}
                {tab.label}
                {tab.count !== null && (
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                    tab.key === 'overdue' && tab.count > 0
                      ? 'bg-red-200 text-red-800'
                      : 'bg-stone-200 text-slate-700'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Dashboard Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-2">
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-900 transition-all hover:shadow-sm">
              <div className="text-xl font-bold text-stone-50 mb-1">{dashboardMetrics.upcoming}</div>
              <div className="text-sm font-medium text-stone-50 uppercase tracking-wide">Upcoming</div>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-900 transition-all hover:shadow-sm">
              <div className="text-xl font-bold text-stone-50 mb-1">{dashboardMetrics.inProgress}</div>
              <div className="text-sm font-medium text-stone-50 uppercase tracking-wide">In Progress</div>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-900 transition-all hover:shadow-sm">
              <div className="text-xl font-bold text-stone-50 mb-1">{dashboardMetrics.needAttention}</div>
              <div className="text-sm font-medium text-stone-50 uppercase tracking-wide">Need Attention</div>
            </div>
          </div>

          {/* Search and Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-4 h-4" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => {
                  console.log('🔧 [DEBUG] Search input onChange triggered', {
                    newValue: e.target.value,
                    oldValue: searchTerm,
                    hasFocus: document.activeElement === searchInputRef.current,
                    timestamp: Date.now()
                  });
                  setSearchTerm(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500 transition-colors"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2.5 text-sm border rounded-lg transition-all font-medium ${
                  showFilters ? 'bg-azure-50 border-azure-300 text-azure-700' : 'border-stone-300 hover:bg-stone-50'
                }`}
              >
                <Filter className="w-4 h-4 mr-2 inline" />
                Filters
              </button>

              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500 transition-colors"
              >
                <option value="due_date-asc">Due Date (Earliest)</option>
                <option value="due_date-desc">Due Date (Latest)</option>
                <option value="title-asc">Title (A-Z)</option>
                <option value="title-desc">Title (Z-A)</option>
                <option value="priority-desc">Priority (High-Low)</option>
                <option value="priority-asc">Priority (Low-High)</option>
                <option value="category-asc">Category</option>
              </select>
            </div>
          </div>

          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-stone-300 rounded-lg"
                >
                  <option value="all">All Categories</option>
                  {filterOptions.categories.map(category => (
                    <option key={category} value={category}>
                      {getCategoryInfo(category).label}
                    </option>
                  ))}
                </select>

                <select
                  value={epicFilter}
                  onChange={(e) => setEpicFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-stone-300 rounded-lg"
                >
                  <option value="all">All Epics</option>
                  {filterOptions.epics.map(epic => (
                    <option key={epic} value={epic}>
                      {getEpicInfo(epic).label}
                    </option>
                  ))}
                </select>

                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-stone-300 rounded-lg"
                >
                  <option value="all">All Priorities</option>
                  {filterOptions.priorities.map(priority => (
                    <option key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </option>
                  ))}
                </select>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Smart Banner System */}
          {activeTab !== 'overdue' && taskCounts.overdue > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-900">
                    Note: {taskCounts.overdue} task{taskCounts.overdue > 1 ? 's are' : ' is'} overdue and need{taskCounts.overdue > 1 ? '' : 's'} attention.
                  </p>
                  <button 
                    onClick={() => setActiveTab('overdue')}
                    className="text-sm text-orange-700 hover:text-orange-800 font-medium underline"
                  >
                    Click the "Overdue" tab to focus on urgent items.
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'overdue' && taskCounts.overdue > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-coral-100 border-2 border-coral-500 rounded-xl"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-red-900">
                      {taskCounts.overdue} Overdue Task{taskCounts.overdue > 1 ? 's' : ''} Need{taskCounts.overdue > 1 ? '' : 's'} Immediate Attention
                    </p>
                    <p className="text-xs text-red-700">
                      Some critical deadlines have passed. Review and prioritize these tasks.
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    if (selectedTasks.size === 0) {
                      // Select all overdue tasks for bulk review
                      const overdueTaskIds = filteredAndSortedTasks.map(task => task.id);
                      setSelectedTasks(new Set(overdueTaskIds));
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Bulk Review →
                </button>
              </div>
            </motion.div>
          )}

          {/* Bulk Actions */}
          {selectedTasks.size > 0 && (
            <div className="mt-4 flex items-center gap-4 p-3 bg-azure-50 rounded-lg">
              <span className="text-sm font-medium text-azure-900">
                {selectedTasks.size} task{selectedTasks.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkAction('complete')}
                  disabled={loading}
                  className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50"
                >
                  Complete
                </button>
                <button
                  onClick={() => handleBulkAction('start')}
                  disabled={loading}
                  className="px-3 py-1 bg-azure-600 text-white text-sm rounded hover:bg-azure-700 disabled:opacity-50"
                >
                  Start
                </button>
                <button
                  onClick={() => handleBulkAction('dismiss')}
                  disabled={loading}
                  className="px-3 py-1 bg-slate-600 text-white text-sm rounded hover:bg-slate-700 disabled:opacity-50"
                >
                  Dismiss
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1 bg-stone-200 text-slate-700 text-sm rounded hover:bg-stone-300"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto">
          {filteredAndSortedTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-stone-500">
              <AlertCircle className="w-12 h-12 mb-4 text-stone-300" />
              <h3 className="text-lg font-medium mb-2">No tasks found</h3>
              <p className="text-sm">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {/* Select All */}
              <div className="flex items-center gap-3 p-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={selectedTasks.size === filteredAndSortedTasks.length}
                  onChange={() => {
                    if (selectedTasks.size === filteredAndSortedTasks.length) {
                      clearSelection();
                    } else {
                      selectAllVisible();
                    }
                  }}
                  className="w-4 h-4"
                />
                <span>Select all ({filteredAndSortedTasks.length} tasks)</span>
              </div>

              {/* Task Items */}
              {filteredAndSortedTasks.map((task) => {
                const categoryInfo = getCategoryInfo(task.category);
                const epicInfo = getEpicInfo(task.epic_id);
                const CategoryIcon = categoryInfo.icon;
                const isSelected = selectedTasks.has(task.id);
                const isCompleted = isTaskCompleted(task);
                const isOverdue = isTaskOverdue(task);
                const daysLate = getDaysLate(task);

                return (
                  <motion.div
                    key={task.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 border rounded-lg transition-all duration-200 ${
                      isCompleted
                        ? isSelected
                          ? 'bg-stone-100 border-stone-300 shadow-sm opacity-75'
                          : 'bg-stone-50 border-stone-200 hover:bg-stone-100 opacity-75'
                        : isSelected 
                          ? isOverdue 
                            ? 'bg-red-50 border-red-300 shadow-sm' 
                            : 'bg-azure-50 border-azure-200 shadow-sm'
                          : isOverdue 
                            ? 'bg-red-25 border-red-200 hover:bg-red-50 hover:shadow-sm' 
                            : 'bg-white border-stone-200 hover:bg-stone-50 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTaskSelection(task.id)}
                        className="mt-1 w-4 h-4"
                      />

                      {/* Category Icon with Overdue Indicator */}
                      <div className="relative">
                        <div className={`mt-0.5 p-1.5 rounded ${
                          isOverdue ? 'bg-red-100 text-red-700' : categoryInfo.color
                        }`}>
                          <CategoryIcon className="w-3 h-3" />
                        </div>
                        {isOverdue && (
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-600 rounded-full"></div>
                        )}
                      </div>

                      {/* Task Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-semibold truncate leading-tight ${
                          isCompleted ? 'text-stone-500 line-through' : isOverdue ? 'text-red-900' : 'text-slate-900'
                        }`}>
                          {task.title}
                        </h4>
                        
                        {task.description && (
                          <p className={`text-sm mt-1.5 line-clamp-2 leading-relaxed ${
                            isCompleted ? 'text-stone-400' : isOverdue ? 'text-red-700' : 'text-slate-600'
                          }`}>
                            {task.description}
                          </p>
                        )}

                        {/* Metadata */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                          {task.college_name && (
                            <span className="flex items-center gap-1">
                              <GraduationCap className="w-3 h-3" />
                              {task.college_name}
                            </span>
                          )}
                          
                          <span className={`px-2 py-1 rounded ${epicInfo.color} flex items-center gap-1`}>
                            {epicInfo.icon && <epicInfo.icon className="w-3 h-3" />}
                            {epicInfo.label}
                          </span>

                          <span className={`px-2 py-1 rounded ${getPriorityColor(task.priority)}`}>
                            {task.priority || 'medium'}
                          </span>

                          {isOverdue && daysLate > 0 && (
                            <span className="px-2 py-1 rounded bg-red-200 text-red-800 font-bold text-xs">
                              {daysLate} DAY{daysLate > 1 ? 'S' : ''} LATE
                            </span>
                          )}

                          <span className={`flex items-center gap-1 ${
                            isOverdue ? 'text-red-600' : 'text-stone-500'
                          }`}>
                            <Calendar className="w-3 h-3" />
                            {getDateDisplay(task)}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {task.completion_status === 'not_started' && task.status !== 'dismissed' && (
                          <>
                            <button
                              onClick={() => handleTaskAction(task.id, 'start')}
                              disabled={loading}
                              className="p-2 text-azure-600 hover:bg-azure-100 rounded transition-colors disabled:opacity-50"
                              title="Start task"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleTaskAction(task.id, 'complete')}
                              disabled={loading}
                              className="p-2 text-emerald-600 hover:bg-emerald-100 rounded transition-colors disabled:opacity-50"
                              title="Mark as complete"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {(task.completion_status === 'in_progress' || task.status === 'in_progress') && (
                          <button
                            onClick={() => handleTaskAction(task.id, 'complete')}
                            disabled={loading}
                            className="p-2 text-emerald-600 hover:bg-emerald-100 rounded transition-colors disabled:opacity-50"
                            title="Mark as complete"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}

                        {task.completion_status !== 'completed' && task.status !== 'dismissed' && (
                          <button
                            onClick={() => handleTaskAction(task.id, 'dismiss')}
                            disabled={loading}
                            className="p-2 text-slate-600 hover:bg-stone-100 rounded transition-colors disabled:opacity-50"
                            title="Dismiss task"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}

                        {/* Status Indicator */}
                        <div className="flex items-center">
                          {isCompleted && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">Done</span>
                            </div>
                          )}
                          {(task.completion_status === 'in_progress' || task.status === 'in_progress') && !isCompleted && (
                            <PlayCircle className="w-5 h-5 text-azure-500" />
                          )}
                          {task.status === 'dismissed' && !isCompleted && (
                            <EyeOff className="w-5 h-5 text-stone-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Memoize the component to prevent re-renders when parent re-renders
export default React.memo(TaskManagerPanel, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if these specific props change
  const propsToCompare = ['isOpen', 'userId', 'initialTab'];

  // Check primitive props
  for (const prop of propsToCompare) {
    if (prevProps[prop] !== nextProps[prop]) {
      console.log('🔧 [DEBUG] TaskManagerPanel memo: Re-rendering due to', prop, 'change');
      return false; // Re-render
    }
  }

  // Check timeline array (compare by reference and length)
  if (prevProps.timeline !== nextProps.timeline) {
    if (prevProps.timeline?.length !== nextProps.timeline?.length) {
      console.log('🔧 [DEBUG] TaskManagerPanel memo: Re-rendering due to timeline length change');
      return false; // Re-render
    }
    console.log('🔧 [DEBUG] TaskManagerPanel memo: timeline reference changed but length same, skipping re-render');
  }

  console.log('🔧 [DEBUG] TaskManagerPanel memo: Props unchanged, preventing re-render');
  return true; // Don't re-render
});